import type { Kick, Zone } from './types';
import { DAY_MS, startOfLocalDay } from './pregnancy';
import { firstCompletion, type Completion } from './countToTen';

export const ZONES: Zone[] = ['upper_left', 'upper_right', 'lower_left', 'lower_right'];

export interface DayStat {
  dayStart: number;
  kicks: number;
  completion: Completion | null;
}

/** Last `days` local days, oldest first. Each day: total kicks + first time 10-in-2h was reached. */
export function dailyStats(kicks: Kick[], now: number, days = 7): DayStat[] {
  const today = startOfLocalDay(now);
  const times = kicks.map(k => k.occurredAt);
  return Array.from({ length: days }, (_, i) => {
    const dayStart = today - (days - 1 - i) * DAY_MS;
    const dayEnd = dayStart + DAY_MS;
    return {
      dayStart,
      kicks: times.filter(t => t >= dayStart && t < dayEnd).length,
      completion: firstCompletion(times, dayStart, dayEnd)
    };
  });
}

export function zoneCounts(kicks: Kick[], since: number): Record<Zone, number> {
  const out: Record<Zone, number> = { upper_left: 0, upper_right: 0, lower_left: 0, lower_right: 0 };
  for (const k of kicks) if (k.zone && k.occurredAt >= since) out[k.zone]++;
  return out;
}

/** Hour-of-day histogram (0–23), useful to show when the baby is most active. */
export function hourHistogram(kicks: Kick[], since: number): number[] {
  const h = new Array<number>(24).fill(0);
  for (const k of kicks) if (k.occurredAt >= since) h[new Date(k.occurredAt).getHours()]!++;
  return h;
}

/** Consecutive days (ending today, or yesterday if today has none yet) with at least one kick. */
export function streakDays(kicks: Kick[], now: number): number {
  const days = new Set(kicks.map(k => startOfLocalDay(k.occurredAt)));
  let d = startOfLocalDay(now);
  if (!days.has(d)) d -= DAY_MS;
  let n = 0;
  while (days.has(d)) { n++; d -= DAY_MS; }
  return n;
}

/** Size of the 3D bump in model units (src/three/belly.ts RX/RY), to map kicks onto a flat front view. */
const BUMP_RX = 1.3, BUMP_RY = 1.38;

export interface KickSummary {
  total: number;
  located: number;                          // kicks with a spot on the bump (Premium / trial)
  topZone: Zone | null;
  topHour: number | null;                   // 0–23, only once there's enough to mean something
  spots: { x: number; y: number }[];        // flat front-view coordinates, -1..1, y up
}

/** What the paywall shows a mother about her own baby. Pure, no judgement of the numbers. */
export function kickSummary(kicks: Kick[], maxSpots = 60): KickSummary {
  const zc = zoneCounts(kicks, 0);
  const topZone = (Object.entries(zc) as [Zone, number][]).sort((a, b) => b[1] - a[1])[0];
  const hist = hourHistogram(kicks, 0);
  const maxH = Math.max(...hist);
  const located = kicks.filter(k => k.x !== null && k.y !== null);
  return {
    total: kicks.length,
    located: located.length,
    topZone: topZone && topZone[1] > 0 ? topZone[0] : null,
    topHour: kicks.length >= 5 && maxH > 0 ? hist.indexOf(maxH) : null,
    spots: located.slice(-maxSpots).map(k => ({
      x: Math.max(-1, Math.min(1, k.x! / BUMP_RX)),
      y: Math.max(-1, Math.min(1, k.y! / BUMP_RY))
    }))
  };
}
